<template lang='pug'>
  .cart-dropdown(
    v-if='cart'
    :style='cartStyle'
    @click.stop=''
  )
    .cart-header
      h3.h3(v-text='$t("navigation.links.bag")')
      button.close(@click='$emit("closeDropdowns")', :id="showAtbTrigger && 'dy-banner-atbd1-button-2'")

    .cart-loading(v-if='cartLoading')
      .loader-spinner

    template(v-else)
      .cart-empty(v-if='lineItems.length === 0')
        .svg-icon.bag-black
        p(v-t='"noItemsInCart"')
        p(v-t='"startFromMakeupSection"')
        .shop-now
          router-link.btn.btn-block(:to='{ name: "categories-show", params: { id: "makeup" } }' v-t='"common.shopNow"' @click.native="closeCartDropdown")

      template(v-else)
        #dy-cart-show-text
        .cart-line-items
          .line-item(v-for='lineItem in displayLineItems' :key='lineItem.id' :data-ge-basket-cartitemid='lineItem.id')
            LineItemCard(
              :line-item='lineItem'
              :product='findLineItemProduct(lineItem)'
              :sample='findLineItemSample(lineItem)'
              :variant='findLineItemVariant(lineItem)'
            )
        CartShippingMessage(:exclusive-offer='shippingOffer' v-if="shippingOffer")

        .cart-totals-and-actions(:class="{ 'without-shipping-msg': !shippingOffer }")
          .cart-total(v-t='{ path: "common.totalWithAmount", args: { amount: cartAmount() } }')
          .cart-loyalty-points(v-if='currentUser && currentUser.attributes' v-t='{ path: "common.loyaltyPointsWithAmount", args: { amount: currentUser.attributes["points-formatted"] } }')
          div(:class="showAtbTrigger ? 'atb-trigger' : 'view-bag-container'")
            .sign-in-container(v-if='showAtbTrigger')
              a#dy-banner-atbd2-button-1(href='/sign_in' v-t='"common.signInCtaLabel"')
              p(v-t='"common.signInCtaMessage"')
            a.btn.btn-primary.btn-block(:id="showAtbTrigger && 'dy-banner-atbd1-button-3'" href='/cart' v-t='"navigation.common.viewBag"')
</template>

<i18n>
{
  "lng_en": {
    "startFromMakeupSection": "Why not start from the makeup section?",
    "noItemsInCart": "Your shopping bag is empty :("
  },
  "lng_id": {
    "startFromMakeupSection": "Why not start from the makeup section?",
    "noItemsInCart": "You have no items in your shopping bag."
  },
  "lng_th": {
    "startFromMakeupSection": "ลองเริ่มจากหมวดหมู่เมคอัพดูไหม?",
    "noItemsInCart": "ถุงช็อปปิ้งของคุณว่างเปล่า :("
  },
  "lng_zh": {
    "startFromMakeupSection": "從化妝品開始選購吧！",
    "noItemsInCart": "你的購物袋未有貨品 :("
  },
  "lng_ko": {
    "startFromMakeupSection": "메이크업 제품부터 쇼핑을 시작해보세요. ",
    "noItemsInCart": "쇼핑백이 비어있습니다 :("
  }
}
</i18n>

<script>
import { mapState, mapGetters } from 'vuex'
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import { humanizePrice } from '@/modules/services/price-support'
import LineItemCard from './line-item-card.vue'
import CartShippingMessage from './cart-shipping-message.vue'

export default {
  components: {
    LineItemCard,
    CartShippingMessage
  },

  data () {
    return {
      cartStyle: {}
    }
  },

  computed: {
    ...mapState({
      cart: state => state.cart.cart,
      lineItems: state => state.cart.lineItems,
      promotionalLineItems: state => state.cart.promotionalLineItems,
      products: state => state.cart.cartProducts,
      variants: state => state.cart.cartVariants,
      samples: state => state.cart.cartSamples,
      cartLoading: state => state.cart.cartLoading,
      currentUser: state => state.user.currentUser,
      brandsLoaded: state => state.brands.brandsLoaded,
      showCartDropdown: state => state.cart.showCartDropdown
    }),
    ...mapGetters(['lineItemsCount', 'exclusiveOffers']),

    shippingOffer () {
      if (isEmpty(this.exclusiveOffers)) return

      return this.exclusiveOffers[0]
    },

    displayLineItems () {
      return this.lineItems.concat(this.promotionalLineItems || [])
    },

    showAtbTrigger () {
      return !this.currentUser && vueSupport.countryCode !== 'kr' && this.lineItems.length > 0
    }
  },

  watch: {
    // watching cart-token so that if the cart changes line items can be reset.
    'cart.attributes.token' (_newVal, oldVal) {
      oldVal && this.resetLineItemsForCartPopup()
    },

    showCartDropdown () {
      if (!this.cart || this.lineItemsCount !== this.displayLineItems.length) {
        this.resetLineItemsForCartPopup()
      }
    }
  },

  created () {
    if (!this.brandsLoaded) {
      // used to retrive line items brands
      this.$store.dispatch('fetchBrands')
    }
  },

  mounted () {
    this.renderDropdownPosition()
    window.addEventListener('resize', this.renderDropdownPosition)
  },

  destroyed () {
    window.removeEventListener('resize', this.renderDropdownPosition)
  },

  methods: {
    humanizePrice,

    watchShowCartDropdown () {
      // we can not unwatch this after the first time because, the disparity could happen
      // again once the user logs in.
      this.$watch('showCartDropdown', () => {
        if (!this.cart || this.lineItemsCount !== this.lineItems.length) {
          this.resetLineItems()
        }
      })
    },

    resetLineItems () {
      this.$store.dispatch('getLineItems')
    },

    resetLineItemsForCartPopup () {
      this.$store.dispatch('getLineItemsForCartPopup')
    },

    renderDropdownPosition () {
      const popupOffset = 330
      const positionLeft = this.$parent.$refs.cartIcon.offsetLeft - popupOffset
      this.cartStyle = { left: `${positionLeft}px` }
    },

    // If li.attributes or li.relationships are undefined or false, it indicates that the response is coming from the bag endpoint.
    findLineItemProduct (li) {
      const type = li.attributes ? li.attributes['line-item-type'] : li.type

      if (type === 'product' || type === 'gwp') {
        const productId = li.relationships ? get(li, 'relationships.product.data.id', -1) : li.id || -1
        return this.products.find(product => product.id === productId)
      }
      return null
    },

    findLineItemVariant (li) {
      const variantId = li.attributes ? get(li, 'attributes["variant-id"]') : li.id

      if (!variantId) { return null }

      return this.variants.find(variant => parseInt(variant.id) === variantId)
    },

    findLineItemSample (li) {
      const type = li.attributes ? li.attributes['line-item-type'] : li.type
      if (type === 'product') {
        return null
      }
      const sampleId = li.relationships ? get(li, 'relationships.sample.data.id', -1) : li.id || -1
      return this.samples.find(sample => sample.id === sampleId)
    },

    cartAmount () {
      return this.cart.attributes['total-pre-shipping']
      ? humanizePrice(this.cart.attributes['total-pre-shipping'])
      : this.cart.attributes['display-total']
    },

    closeCartDropdown () {
      this.$store.commit('setShowCartDropdown', false)
    }
  }
}
</script>

<style lang='scss' scoped>
  .line-item {
    border-top: 1px solid $grey;

    &:first-child {
      border-top: 0;
    }
  }

  .cart-loading {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
  }

  .cart-dropdown {
    width: 360px;
    position: absolute;
    top: 26px;
    border: 1px solid $grey;
    box-shadow: 0 2px 10px 0 $grey;
    background-color: $white;
    z-index: $zindex-fixed;
    margin-top: 9px;
    padding-bottom: 25px;
    color: $black;

    &::before {
      position: absolute;
      top: -7px;
      left: auto;
      right: 9px;
      content: '';
      border-left: 7px solid transparent;
      border-right: 7px solid transparent;
      border-bottom: 7px solid $grey;
      border-bottom-color: rgba(0, 0, 0, 0.2);
      display: inline-block;
    }

    &::after {
      position: absolute;
      top: -6px;
      left: auto;
      right: 10px;
      content: '';
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-bottom: 6px solid $white;
      display: inline-block;
    }

    .cart-line-items, .cart-exclusive-offers {
      max-height: 310px;
      width: 100%;
      padding-left: 15px;
      padding-right: 15px;
      overflow-y: auto;

      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-track {
        background: $grey-light;
      }

      &::-webkit-scrollbar-thumb {
        background: $grey-mid;
      }
    }

    .cart-totals-and-actions {
      width: 100%;
      text-align: center;
      padding-left: 17px;
      padding-right: 16px;

      &.without-shipping-msg {
        border-top: 1px solid $grey;
      }
      .view-bag-container {
        margin-top: 25px;
      }

      .atb-trigger {
        display: grid;
        grid-template-columns: 1fr 1.1fr;
        align-items: center;
        gap: 12px;
        padding-top: 20px;
        margin-block: 24px -5px;
        border-top: 2px solid #ccc;
        text-align: center;
        .sign-in-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 4px;
          font-size: 16px;
          line-height: 20px;
          min-height: 70px;
          padding-right: 12px;
          border-right: 1px solid #ccc;

          a {
            text-decoration: underline;
          }
        }
      }
    }

    .cart-empty {
      height: 400px;
      margin: 120px auto;
      text-align: center;
      padding-left: 17px;
      padding-right: 15.5px;

      a {
        @include font-weight-bold;
      }

      p {
        margin-top: 18px;
        margin-bottom: 20px;
        @include font-weight-book;
        font-size: $font-size-root;
      }
    }

    .shop-now {
      margin: 20px 90px 145px;
    }

    .cart-total {
      @include font-weight-bold;
      font-size: 20px;
      text-transform: uppercase;
      text-align: center;
      margin-top: 17px;
      margin-bottom: 5px;
    }

    .cart-loyalty-points {
      @include font-weight-book;
      font-size: $font-size-root;
      color: $black;
    }
  }

  .cart-header {
    background-color: $grey-light;
    padding: 12px 17px;
    text-transform: uppercase;

    .close {
      margin-top: -30px;
    }
  }
</style>
